import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { File as FileIcon, Upload } from 'lucide-react';
import { FILE_MIME_TYPES } from '@nordinsight/implant-core';
import { Button } from '../../ui/button';
import LoadingRing from '../LoadingRing';
import LinkPicker from './LinkPicker';
import { cn } from '../../../lib/utils';
import { useToast } from '../../../hooks/use-toast';
import {
    getDepartmentFiles,
    getUploadUrl,
    uploadFile,
} from '../../../services/api/fileService';
import { getFileMimeType } from '../../../utils/files';
import { LinkedFileDTO } from '../../../services/apiService';

const SUPPORTED_MIME_TYPES = new Set<string>([
    FILE_MIME_TYPES.pdf,
    FILE_MIME_TYPES.doc,
    FILE_MIME_TYPES.docx,
    FILE_MIME_TYPES.jpeg,
    FILE_MIME_TYPES.png,
    FILE_MIME_TYPES.pptx,
    FILE_MIME_TYPES.xlsx,
]);
const UPLOAD_ACCEPT = [
    ...SUPPORTED_MIME_TYPES,
    '.doc',
    '.docx',
    '.pptx',
    '.xlsx',
].join(',');
const PICKABLE_TYPES = ['document', 'image', 'other'];

const loadDepartmentFiles = async (): Promise<LinkedFileDTO[]> => {
    const files: LinkedFileDTO[] = await getDepartmentFiles();
    return files.filter((f) => PICKABLE_TYPES.includes(f.documentType));
};

interface FilePickerProps {
    compact?: boolean;
    value: LinkedFileDTO[];
    onChange: (files: LinkedFileDTO[]) => void;
}

export default function FilePicker({
    value,
    onChange,
    compact,
}: FilePickerProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [dragging, setDragging] = useState(false);

    const upload = async (files: FileList | null) => {
        const list = Array.from(files ?? []);
        if (list.length === 0) return;
        if (list.some((f) => !SUPPORTED_MIME_TYPES.has(getFileMimeType(f)))) {
            toast({
                title: t('pickers.files.unsupportedType'),
                variant: 'destructive',
            });
            return;
        }
        setUploading(true);
        let next = value;
        try {
            for (const file of list) {
                const info = await getUploadUrl({
                    ownerType: 'department',
                    filename: file.name,
                    fileType: getFileMimeType(file),
                });
                if (!info.url || !info.file?._id) {
                    throw new Error('Upload URL unavailable');
                }
                if ((await uploadFile(info.url, file)) !== 'OK') {
                    throw new Error('Upload failed');
                }
                next = [...next, info.file];
                onChange(next);
            }
        } catch {
            toast({
                title: t('pickers.files.uploadFailed'),
                variant: 'destructive',
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div
            className="rounded-lg"
            onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                upload(e.dataTransfer.files);
            }}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                className="hidden"
                onChange={(e) => {
                    upload(e.target.files);
                    e.target.value = '';
                }}
            />
            {!compact && (
                <button
                    type="button"
                    disabled={uploading}
                    onClick={() => inputRef.current?.click()}
                    className={cn(
                        'mb-3 flex w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed bg-input px-4 py-6 text-sm text-foreground transition-colors',
                        dragging
                            ? 'border-foreground bg-accent'
                            : 'border-control hover:border-foreground hover:bg-accent'
                    )}
                >
                    {uploading ? (
                        <LoadingRing size="small" />
                    ) : (
                        <>
                            <Upload
                                className="h-5 w-5 text-subtle-foreground"
                                aria-hidden="true"
                            />
                            <span className="font-medium">
                                {t(
                                    dragging
                                        ? 'pickers.files.dropHere'
                                        : 'pickers.files.dropzone'
                                )}
                            </span>
                            {!dragging && (
                                <span className="text-[11px] font-medium text-subtle-foreground">
                                    {t('pickers.files.dropzoneFormats')}
                                </span>
                            )}
                        </>
                    )}
                </button>
            )}
            <LinkPicker
                compact={compact}
                value={value}
                onChange={onChange}
                load={loadDepartmentFiles}
                label={(f) => f.filename}
                meta={(f) => f.filename.split('.').pop()?.toUpperCase()}
                icon={FileIcon}
                text={{
                    empty: t('pickers.files.empty'),
                    add: t('pickers.files.add'),
                    search: t('pickers.files.search'),
                    noMatching: t('pickers.files.noMatching'),
                    remove: t('pickers.files.remove'),
                    loadFailed: t('pickers.files.loadFailed'),
                }}
                actions={
                    compact ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 border-control px-3 text-xs"
                            disabled={uploading}
                            onClick={() => inputRef.current?.click()}
                        >
                            {uploading ? (
                                <LoadingRing size="small" />
                            ) : (
                                <>
                                    <Upload
                                        className="mr-1 h-3.5 w-3.5"
                                        aria-hidden="true"
                                    />
                                    {t('pickers.files.upload')}
                                </>
                            )}
                        </Button>
                    ) : undefined
                }
            />
        </div>
    );
}
